import { Button } from 'ui';
import { Checkbox, TableCell, TableRow } from '@mui/material';
import { useDrag, useDragLayer, useDrop } from 'react-dnd';
import React, { useRef } from 'react';

import { CollectionSummary, OrderingEntrySummary } from './rowSummaryTypes';
import { DRAG_TYPE_ORDERING, DRAG_TYPE_UNORDERED } from './dragTypes';
import { STATUS_LABELS } from 'tools/jce-geography/JceVenueCollectionFormRoute';

export interface DraggableOrderingRowProps {
  entry: OrderingEntrySummary;
  index: number;
  isSaving: boolean;
  editButton?: React.ReactElement;
  onMove: (dragIndex: number, hoverIndex: number) => void;
  onDrop: () => void;
  onRemove: () => void;
  onDropUnordered: (collectionSummary: CollectionSummary, atIndex: number) => void;
  onLeaderboardChange?: (value: boolean) => void;
}

export const DraggableOrderingRow: React.FC<DraggableOrderingRowProps> = ({
  entry,
  index,
  isSaving,
  editButton,
  onMove,
  onDrop,
  onRemove,
  onDropUnordered,
  onLeaderboardChange,
}) => {
  const ref = useRef<HTMLTableRowElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE_ORDERING,
    item: { index, entry },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOverUnordered }, drop] = useDrop<
    { index: number } | { collectionSummary: CollectionSummary },
    void,
    { isOverUnordered: boolean }
  >({
    accept: [DRAG_TYPE_ORDERING, DRAG_TYPE_UNORDERED],
    collect: (monitor) => ({
      isOverUnordered:
        monitor.isOver({ shallow: true }) && monitor.getItemType() === DRAG_TYPE_UNORDERED,
    }),
    hover(item) {
      if ('index' in item) {
        if (isSaving || !ref.current || item.index === index) {
          return;
        }
        onMove(item.index, index);
        item.index = index;
      }
    },
    drop(item) {
      if ('collectionSummary' in item) {
        if (!isSaving) {
          onDropUnordered(item.collectionSummary, index);
        }
      } else {
        if (!isSaving) {
          onDrop();
        }
      }
    },
  });

  drag(drop(ref));

  return (
    <TableRow
      ref={ref}
      style={{
        opacity: isDragging ? 0.4 : 1,
        cursor: isSaving ? 'wait' : 'grab',
        background: isOverUnordered ? '#e8f5e9' : isDragging ? '#f5f5f5' : undefined,
      }}
    >
      <TableCell style={{ width: 32, color: '#aaa', userSelect: 'none' }}>☰</TableCell>
      <TableCell>{entry.position ?? index + 1}</TableCell>
      <TableCell>{entry.collection ? entry.collection.id : '—'}</TableCell>
      <TableCell>{entry.collection ? entry.collection.name : entry.specialCollection}</TableCell>
      <TableCell>{entry.collection?.shortName ?? '—'}</TableCell>
      <TableCell>
        {entry.collection
          ? entry.collection.status
            ? STATUS_LABELS.get(entry.collection.status)
            : '—'
          : 'Algorithmic'}
      </TableCell>
      <TableCell style={{ width: 80 }}>
        {entry.collection && (
          <Checkbox
            checked={entry.isDesignatedForLeaderboard ?? false}
            disabled={isSaving}
            onChange={(e) => onLeaderboardChange?.(e.target.checked)}
            size="small"
          />
        )}
      </TableCell>
      <TableCell style={{ width: 80 }}>{editButton ?? null}</TableCell>
      <TableCell style={{ width: 80 }}>
        <Button size="small" disabled={isSaving} onClick={onRemove}>
          Remove
        </Button>
      </TableCell>
    </TableRow>
  );
};

// ─── DraggableUnorderedRow ────────────────────────────────────────────────────

interface DraggableUnorderedRowProps {
  collectionSummary: CollectionSummary;
  isSaving: boolean;
  onDropOrdering: (entry: OrderingEntrySummary) => void;
  editButton?: React.ReactElement;
}

export const DraggableUnorderedRow: React.FC<DraggableUnorderedRowProps> = ({
  collectionSummary,
  isSaving,
  onDropOrdering,
  editButton,
}) => {
  const ref = useRef<HTMLTableRowElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE_UNORDERED,
    item: { collectionSummary },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOverOrdering }, drop] = useDrop<
    { index: number; entry: OrderingEntrySummary },
    void,
    { isOverOrdering: boolean }
  >({
    accept: DRAG_TYPE_ORDERING,
    collect: (monitor) => ({
      isOverOrdering: monitor.isOver({ shallow: true }),
    }),
    drop(item) {
      if (!isSaving) {
        onDropOrdering(item.entry);
      }
    },
  });

  drag(drop(ref));

  return (
    <TableRow
      ref={ref}
      style={{
        opacity: isDragging ? 0.4 : 1,
        cursor: isSaving ? 'wait' : 'grab',
        background: isOverOrdering ? '#fff3e0' : isDragging ? '#f5f5f5' : undefined,
      }}
    >
      <TableCell style={{ width: 32, color: '#aaa', userSelect: 'none' }}>☰</TableCell>
      <TableCell>{collectionSummary.id}</TableCell>
      <TableCell>{collectionSummary.name}</TableCell>
      <TableCell>{collectionSummary.shortName ?? '—'}</TableCell>
      <TableCell>{collectionSummary.status}</TableCell>
      <TableCell style={{ width: 80 }}>{editButton}</TableCell>
    </TableRow>
  );
};

// ─── Trailing drop zone (append to ordered list) ─────────────────────────────

/**
 * Rendered as the last row of the ordering TableBody. Accepts DRAG_TYPE_UNORDERED
 * drops so that a collection can be appended after the last ordered entry.
 */
export const OrderingEntriesTrailingDropZone: React.FC<{
  colSpan: number;
  onDrop: (collectionSummary: CollectionSummary) => void;
}> = ({ colSpan, onDrop }) => {
  const ref = useRef<HTMLTableRowElement>(null);

  const isDraggingUnordered = useDragLayer(
    (monitor) => monitor.isDragging() && monitor.getItemType() === DRAG_TYPE_UNORDERED
  );

  const [{ isOver }, drop] = useDrop<
    { collectionSummary: CollectionSummary },
    void,
    { isOver: boolean }
  >({
    accept: DRAG_TYPE_UNORDERED,
    collect: (monitor) => ({ isOver: monitor.isOver() }),
    drop(item) {
      onDrop(item.collectionSummary);
    },
  });
  drop(ref);

  return (
    <TableRow ref={ref}>
      <TableCell
        colSpan={colSpan}
        style={
          isDraggingUnordered
            ? {
                height: 40,
                padding: '0 16px',
                border: '2px dashed',
                borderColor: isOver ? '#4caf50' : '#ccc',
                borderRadius: 4,
                color: isOver ? '#4caf50' : '#bbb',
                textAlign: 'center',
                background: isOver ? '#f1f8f1' : undefined,
                transition: 'all 0.15s',
                fontSize: 12,
              }
            : { padding: 0, height: 0, border: 'none', lineHeight: 0 }
        }
      >
        {isDraggingUnordered ? (isOver ? 'Drop to add at end' : 'Drop here to add at end') : null}
      </TableCell>
    </TableRow>
  );
};

// ─── Empty-state drop zones ───────────────────────────────────────────────────

/** Shown when there are no ordering entries; accepts unordered collection drops. */
export const OrderingEntriesEmptyDropZone: React.FC<{
  onDrop: (collectionSummary: CollectionSummary) => void;
}> = ({ onDrop }) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [{ isOver }, drop] = useDrop<
    { collectionSummary: CollectionSummary },
    void,
    { isOver: boolean }
  >({
    accept: DRAG_TYPE_UNORDERED,
    collect: (monitor) => ({ isOver: monitor.isOver() }),
    drop(item) {
      onDrop(item.collectionSummary);
    },
  });
  drop(ref);
  return (
    <p
      ref={ref}
      style={{
        padding: '16px',
        border: '2px dashed',
        borderColor: isOver ? '#4caf50' : '#ccc',
        borderRadius: 4,
        color: isOver ? '#4caf50' : '#888',
        textAlign: 'center',
        background: isOver ? '#f1f8f1' : undefined,
        transition: 'all 0.15s',
      }}
    >
      {isOver
        ? 'Drop to add as first ordering entry'
        : 'No ordering entries yet. Drag a collection here to add one.'}
    </p>
  );
};

/** Shown when there are no unordered collections; accepts ordering entry drops. */
export const UnorderedCollectionsEmptyDropZone: React.FC<{
  onDrop: (entry: OrderingEntrySummary) => void;
}> = ({ onDrop }) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [{ isOver }, drop] = useDrop<
    { index: number; entry: OrderingEntrySummary },
    void,
    { isOver: boolean }
  >({
    accept: DRAG_TYPE_ORDERING,
    collect: (monitor) => ({ isOver: monitor.isOver() }),
    drop(item) {
      onDrop(item.entry);
    },
  });
  drop(ref);
  return (
    <p
      ref={ref}
      style={{
        padding: '16px',
        border: '2px dashed',
        borderColor: isOver ? '#ff9800' : '#ccc',
        borderRadius: 4,
        color: isOver ? '#ff9800' : '#888',
        textAlign: 'center',
        background: isOver ? '#fff8f0' : undefined,
        transition: 'all 0.15s',
      }}
    >
      {isOver
        ? 'Drop to remove from ordering'
        : 'No unordered collections. Drag an ordering entry here to remove it.'}
    </p>
  );
};
